import Select, { OnChangeValue } from 'react-select';
import { useAsyncResource } from 'use-async-resource';
import { useTranslation } from 'react-i18next';
import { Filter, Option, User } from '../models/table-models';
import { ALL_OUTCOMES, CardTransactionOutcome } from '../models/card-attempt-models';
import { useGetUsers } from '../utils/users-hooks';

export interface FilterMultiSelectProps {
    filter: Filter;
    setFilter: React.Dispatch<React.SetStateAction<Filter>>;
}

const outcomeOptions: Option[] = ALL_OUTCOMES.map((o) => ({ value: o, name: o }));

/**
 * Filter component for the CardAttempts admin view. Two side-by-side selects:
 * user picker (Filter.id → filterUserId) and outcome picker (Filter.tags →
 * outcome). Backend currently honours one outcome at a time; multi-select is
 * exposed for forward-compat.
 */
export const CardAttemptsFilterSelect = ({ filter, setFilter }: FilterMultiSelectProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getUsers] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const { items: users } = getUsers();

    const onOutcomeChange = (value: OnChangeValue<Option, true>) => {
        const selected = value.map(({ value: v }) => v);
        setFilter({ ...filter, tags: selected });
    };

    const onUserChange = (user: OnChangeValue<User, false>) => {
        const id = user?.id ? String(user.id) : undefined;
        setFilter({ ...filter, id });
    };

    const selectedTags = (filter.tags ?? [])
        .map((t) => outcomeOptions.find((o) => o.value === t))
        .filter((o): o is Option => Boolean(o));

    const selectedUser = users.find((u) => String(u.id) === filter.id) ?? null;

    return (
        <div className='w-100 d-flex justify-content-end gap-2 flex-wrap'>
            <Select<User, false>
                className='logs-select'
                placeholder={t('distributor')}
                isClearable
                value={selectedUser}
                options={users}
                getOptionLabel={(u) => u.name}
                getOptionValue={(u) => String(u.id)}
                onChange={onUserChange}
                backspaceRemovesValue
            />
            <Select
                className='logs-select'
                placeholder='Filter by outcome'
                isMulti
                isClearable
                value={selectedTags}
                options={outcomeOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.value as CardTransactionOutcome}
                onChange={onOutcomeChange}
                backspaceRemovesValue
            />
        </div>
    );
};
